'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import {
  Loader2, Truck, ShieldCheck,
  FileText, MapPin, ArrowLeft, ChevronRight, Mail, KeyRound, Smartphone,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

type LoginRole = 'admin' | 'staff' | null;
type LoginStep = 'role' | 'email' | 'otp' | 'totp';

const stats = [
  { id: 's1', value: '500+', label: 'Vehicles Tracked' },
  { id: 's2', value: '12K+', label: 'Invoices Generated' },
  { id: 's3', value: '99.9%', label: 'Uptime' },
];

export default function LoginPageClient() {
  const [selectedRole, setSelectedRole] = useState<LoginRole>(null);
  const [step, setStep] = useState<LoginStep>('role');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpFactorId, setTotpFactorId] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [otpError, setOtpError] = useState('');
  const [totpError, setTotpError] = useState('');
  const { signInWithOtp, verifyOtp } = useAuth();
  const router = useRouter();
  const supabase = createClient();

  const handleRoleSelect = (role: LoginRole) => {
    setSelectedRole(role);
    setStep('email');
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      await signInWithOtp(email);
      toast.success('OTP sent! Check your email inbox.');
      setStep('otp');
    } catch (err: any) {
      const msg = err?.message || 'Failed to send OTP. Please try again.';
      if (msg.toLowerCase().includes('not found') || msg.toLowerCase().includes('signups not allowed')) {
        toast.error('This email is not registered. Please contact your administrator.');
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');
    if (!otp || otp.length < 6) {
      setOtpError('Please enter the 6-digit OTP from your email');
      return;
    }
    setLoading(true);
    try {
      await verifyOtp(email, otp);

      // Check if this user has TOTP enrolled (admin 2FA)
      const { data: mfaData } = await supabase.auth.mfa.listFactors();
      const verifiedTotp = mfaData?.totp?.find((f: any) => f.status === 'verified');

      if (verifiedTotp) {
        // Admin has 2FA — require TOTP verification
        setTotpFactorId(verifiedTotp.id);
        setStep('totp');
        toast.info('Please enter your authenticator code to complete sign-in.');
      } else {
        toast.success('Welcome to Dehuri Travels!');
        router.push('/dashboard');
        router.refresh();
      }
    } catch (err: any) {
      const msg = err?.message || 'Invalid or expired OTP. Please try again.';
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    setTotpError('');
    if (!totpCode || totpCode.length !== 6) {
      setTotpError('Please enter the 6-digit code from your authenticator app');
      return;
    }
    setLoading(true);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: totpFactorId });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: totpFactorId,
        challengeId: challengeData.id,
        code: totpCode,
      });
      if (verifyError) throw verifyError;

      toast.success('Welcome to Dehuri Travels!');
      router.push('/dashboard');
      router.refresh();
    } catch (err: any) {
      const msg = err?.message || 'Invalid authenticator code. Please try again.';
      setTotpError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await signInWithOtp(email);
      toast.success('New OTP sent! Check your email inbox.');
      setOtp('');
      setOtpError('');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (step === 'totp') {
      // Sign out and go back to role selection since OTP was already consumed
      supabase.auth.signOut().then(() => {
        setStep('role');
        setSelectedRole(null);
        setEmail('');
        setOtp('');
        setTotpCode('');
        setTotpFactorId('');
      });
    } else if (step === 'otp') {
      setStep('email');
      setOtp('');
      setOtpError('');
    } else if (step === 'email') {
      setStep('role');
      setSelectedRole(null);
      setEmail('');
      setEmailError('');
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background">
      {/* Left branding panel */}
      <div className="lg:w-[480px] xl:w-[520px] shrink-0 gradient-primary flex flex-col justify-between p-10 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full bg-white/5 -translate-y-1/3 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-56 h-56 rounded-full bg-white/5 translate-y-1/3 -translate-x-1/3 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 w-96 h-96 rounded-full bg-white/[0.03] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />

        {/* Top: Logo + Name */}
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
              <AppLogo size={32} />
            </div>
            <div>
              <p className="text-xs font-semibold text-white/50 uppercase tracking-widest">Welcome to</p>
              <h1 className="text-2xl font-bold text-white tracking-tight leading-none">Dehuri Travels</h1>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-4xl font-bold text-white leading-tight mb-3">
              Your Fleet.<br />
              <span className="text-accent">Your Business.</span><br />
              All in One Place.
            </h2>
            <p className="text-sm text-white/65 leading-relaxed">
              Manage vehicles, generate GST-compliant invoices, track drivers, and file quarterly returns — built for transport contractors across Odisha.
            </p>
          </div>

          {/* Feature highlights */}
          <div className="space-y-3 mb-10">
            {[
              { icon: Truck, text: 'Fleet & Vehicle Compliance Tracking' },
              { icon: FileText, text: 'GST Invoice Generation & Filing' },
              { icon: ShieldCheck, text: 'Document Vault & Expiry Alerts' },
              { icon: MapPin, text: 'MCL, NALCO & Civil Deployment Management' },
            ].map((f, i) => {
              const FIcon = f.icon;
              return (
                <div key={`feat-${i}`} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <FIcon size={15} className="text-accent" />
                  </div>
                  <span className="text-sm text-white/75 font-medium">{f.text}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom: Stats */}
        <div className="relative z-10">
          <div className="grid grid-cols-3 gap-4">
            {stats.map((s) => (
              <div key={s.id} className="text-center">
                <p className="text-2xl font-bold text-white">{s.value}</p>
                <p className="text-[11px] text-white/50 font-medium mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 overflow-y-auto">
        <div className="w-full max-w-md">

          {/* Mobile header */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
              <AppLogo size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Dehuri Travels</h1>
              <p className="text-xs text-muted-foreground">Business Management</p>
            </div>
          </div>

          {/* ── STEP 1: Role Selection ── */}
          {step === 'role' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Sign In</h2>
                <p className="text-sm text-muted-foreground mt-1">Choose your access level to continue</p>
              </div>

              {/* Admin card */}
              <button
                onClick={() => handleRoleSelect('admin')}
                className="w-full group relative flex items-center gap-4 p-5 rounded-2xl border-2 border-border bg-card hover:border-primary hover:shadow-md transition-all duration-200 text-left"
              >
                <div className="w-14 h-14 rounded-xl gradient-primary flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform duration-200">
                  <ShieldCheck size={26} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-base font-bold text-foreground">Admin Login</p>
                  <p className="text-sm text-muted-foreground mt-0.5">Owner / Partner — Full access to all modules</p>
                </div>
                <ChevronRight size={20} className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-200 shrink-0" />
              </button>

              {/* Staff card */}
              <button
                onClick={() => handleRoleSelect('staff')}
                className="w-full group relative flex items-center gap-4 p-5 rounded-2xl border-2 border-border bg-card hover:border-primary hover:shadow-md transition-all duration-200 text-left"
              >
                <div className="w-14 h-14 rounded-xl bg-accent/15 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform duration-200">
                  <Truck size={26} className="text-accent" />
                </div>
                <div className="flex-1">
                  <p className="text-base font-bold text-foreground">Staff Login</p>
                  <p className="text-sm text-muted-foreground mt-0.5">Staff Member — Operations & data entry access</p>
                </div>
                <ChevronRight size={20} className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-200 shrink-0" />
              </button>

              <p className="text-center text-xs text-muted-foreground pt-2">
                Dehuri Travels — Secure Business Portal
              </p>
            </div>
          )}

          {/* ── STEP 2: Email Entry ── */}
          {step === 'email' && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <button
                  onClick={goBack}
                  className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary transition-all duration-150"
                  aria-label="Go back"
                >
                  <ArrowLeft size={15} />
                </button>
                <div>
                  <h2 className="text-xl font-bold text-foreground">
                    {selectedRole === 'admin' ? 'Admin Login' : 'Staff Login'}
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    {selectedRole === 'admin' ? 'Owner / Partner access' : 'Staff member access'}
                  </p>
                </div>
              </div>

              {/* Role badge */}
              <div className={`flex items-center gap-2.5 px-4 py-3 rounded-xl ${
                selectedRole === 'admin' ? 'bg-primary/8 border border-primary/20' : 'bg-accent/8 border border-accent/20'
              }`}>
                {selectedRole === 'admin'
                  ? <ShieldCheck size={16} className="text-primary shrink-0" />
                  : <Truck size={16} className="text-accent shrink-0" />
                }
                <span className={`text-sm font-semibold ${selectedRole === 'admin' ? 'text-primary' : 'text-accent'}`}>
                  {selectedRole === 'admin' ? 'Administrator — Full Access' : 'Staff Member — Operations Access'}
                </span>
              </div>

              {/* OTP info banner */}
              <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-blue-50 border border-blue-100">
                <Mail size={16} className="text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700 leading-relaxed">
                  Enter your registered email address. We'll send a <strong>6-digit OTP</strong> to verify your identity — no password needed.
                </p>
              </div>

              <form onSubmit={handleSendOtp} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    Email Address <span className="text-negative">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                    placeholder="yourname@dehuritravels.in"
                    autoFocus
                    className="w-full px-3 py-2.5 text-sm bg-muted border border-transparent rounded-lg focus:border-primary focus:outline-none focus:bg-card transition-all duration-150"
                  />
                  {emailError && <p className="text-xs text-negative mt-1">{emailError}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-150"
                >
                  {loading ? (
                    <><Loader2 size={15} className="animate-spin" /> Sending OTP...</>
                  ) : (
                    <><Mail size={15} /> Send OTP to Email</>
                  )}
                </button>
              </form>

              <p className="text-center text-xs text-muted-foreground">
                Dehuri Travels — Secure Business Portal
              </p>
            </div>
          )}

          {/* ── STEP 3: OTP Verification ── */}
          {step === 'otp' && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <button
                  onClick={goBack}
                  className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary transition-all duration-150"
                  aria-label="Go back"
                >
                  <ArrowLeft size={15} />
                </button>
                <div>
                  <h2 className="text-xl font-bold text-foreground">Verify OTP</h2>
                  <p className="text-xs text-muted-foreground">Enter the code sent to your email</p>
                </div>
              </div>

              {/* Email confirmation */}
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-green-50 border border-green-100">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                  <Mail size={15} className="text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-green-800">OTP sent successfully</p>
                  <p className="text-xs text-green-600 mt-0.5">Check inbox for <strong>{email}</strong></p>
                </div>
              </div>

              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    6-Digit OTP <span className="text-negative">*</span>
                  </label>
                  <div className="relative">
                    <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtp(val);
                        setOtpError('');
                      }}
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      autoFocus
                      className="w-full pl-10 pr-3 py-2.5 text-sm bg-muted border border-transparent rounded-lg focus:border-primary focus:outline-none focus:bg-card transition-all duration-150 tracking-[0.3em] font-mono text-center text-lg"
                    />
                  </div>
                  {otpError && <p className="text-xs text-negative mt-1">{otpError}</p>}
                  <p className="text-xs text-muted-foreground mt-1.5">OTP is valid for 10 minutes</p>
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.length < 6}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-150"
                >
                  {loading ? (
                    <><Loader2 size={15} className="animate-spin" /> Verifying...</>
                  ) : (
                    <><ShieldCheck size={15} /> Verify & Continue</>
                  )}
                </button>
              </form>

              <div className="text-center">
                <p className="text-xs text-muted-foreground">Didn't receive the code?</p>
                <button
                  onClick={handleResendOtp}
                  disabled={loading}
                  className="text-xs font-semibold text-primary hover:text-primary/80 disabled:opacity-50 mt-1 transition-colors duration-150"
                >
                  Resend OTP
                </button>
              </div>

              <p className="text-center text-xs text-muted-foreground">
                Dehuri Travels — Secure Business Portal
              </p>
            </div>
          )}

          {/* ── STEP 4: TOTP Verification (Admin 2FA) ── */}
          {step === 'totp' && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <button
                  onClick={goBack}
                  className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary transition-all duration-150"
                  aria-label="Go back"
                >
                  <ArrowLeft size={15} />
                </button>
                <div>
                  <h2 className="text-xl font-bold text-foreground">Two-Factor Authentication</h2>
                  <p className="text-xs text-muted-foreground">Admin account protection</p>
                </div>
              </div>

              {/* 2FA info banner */}
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/5 border border-primary/20">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Smartphone size={15} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">Authenticator Required</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Open your authenticator app and enter the 6-digit code</p>
                </div>
              </div>

              <form onSubmit={handleVerifyTotp} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    Authenticator Code <span className="text-negative">*</span>
                  </label>
                  <div className="relative">
                    <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      value={totpCode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setTotpCode(val);
                        setTotpError('');
                      }}
                      placeholder="000000"
                      maxLength={6}
                      autoFocus
                      className="w-full pl-10 pr-3 py-2.5 text-sm bg-muted border border-transparent rounded-lg focus:border-primary focus:outline-none focus:bg-card transition-all duration-150 tracking-[0.4em] font-mono text-center text-lg"
                    />
                  </div>
                  {totpError && <p className="text-xs text-negative mt-1">{totpError}</p>}
                  <p className="text-xs text-muted-foreground mt-1.5">Code refreshes every 30 seconds</p>
                </div>

                <button
                  type="submit"
                  disabled={loading || totpCode.length !== 6}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-150"
                >
                  {loading ? (
                    <><Loader2 size={15} className="animate-spin" /> Verifying...</>
                  ) : (
                    <><ShieldCheck size={15} /> Verify & Sign In</>
                  )}
                </button>
              </form>

              <p className="text-center text-xs text-muted-foreground">
                Dehuri Travels — Secure Business Portal
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}